import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Tag, X } from "lucide-react";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/atoms/Button";
import { Tooltip } from "@/components/atoms/Tooltip";
import { PageHeader } from "@/components/molecules/PageHeader";
import { usePageScope, useSettingsStore } from "@/store/settingsStore";
import { useScopeOptions } from "@/hooks/useScopeOptions";
import { Pagination } from "@/components/molecules/Pagination";
import { RefreshButton } from "@/components/molecules/RefreshButton";
import { SavedFiltersButton } from "@/components/molecules/SavedFiltersButton";
import { FilterScope } from "@/components/molecules/FilterBar";
import { useContactSelection, useContactsList } from "./contactsHooks";
import { ContactsFilters } from "./ContactsFilters";
import { ContactsTable } from "./ContactsTable";
import { ContactApplyLabelDialog } from "./ContactApplyLabelDialog";
import { ContactDetailDrawer } from "./ContactDetailDrawer";
import { type ContactDeletionImpact } from "@/types/contactDeletion";
import type { RowSelection } from "@/components/organisms/DataTable";
import type { ContactSummary } from "@/types/entities";
import { useUiStore } from "@/store/uiStore";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { useScopeLabels } from "@/hooks/useScopeLabels";
import { useCampaignsLabelsEnabled } from "@/modules/campaigns/campaignsLabelsAccess";
import { useFilterUrlSync } from "@/hooks/useFilterUrlSync";
import { initialPageWithUrlSort, sortDefaultsFromPage, useSortUrlSync } from "@/hooks/useSortUrlSync";
import type { PageRequest } from "@/types/api";
import {
  CLEARED_CONTACT_FILTER,
  CONTACT_FILTER_PARAM_KEYS,
  contactFilterUrlSyncConfig,
  parseContactFilterFromSearchParams,
} from "./contactFilterParams";
import { useCreateShortcut } from "@/hooks/useCreateShortcut";
import { formatPrimaryShortcut } from "@/lib/keyboardShortcut";

/** Legal entities implied by the contacts scope picker (whole entities + branch picks). */
function legalEntityIdsInScope(
  legalEntityIds: string[],
  branchIds: string[],
  branchToLegalEntity: Record<string, string>,
): string[] {
  const ids = new Set(legalEntityIds.filter(Boolean));
  for (const branchId of branchIds) {
    const legalEntityId = branchToLegalEntity[branchId];
    if (legalEntityId) ids.add(legalEntityId);
  }
  return [...ids];
}

const CONTACTS_INITIAL_PAGE: PageRequest = { page: 0, size: 30, sort: "atlasModifiedAt", direction: "desc" };
const CONTACTS_SORT_DEFAULTS = sortDefaultsFromPage(CONTACTS_INITIAL_PAGE);

function hasActiveContactFilterParams(params: URLSearchParams): boolean {
  return Object.keys(parseContactFilterFromSearchParams(params)).length > 0;
}

export function ContactsPage() {
  const { startPageLoading, stopPageLoading } = useUiStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialFilter] = useState(() => parseContactFilterFromSearchParams(searchParams));
  const [initialPage] = useState(() => initialPageWithUrlSort(searchParams, CONTACTS_INITIAL_PAGE));
  const list = useContactsList(initialFilter, initialPage);
  useSortUrlSync(list.pageRequest.sort, list.pageRequest.direction, list.setSort, CONTACTS_SORT_DEFAULTS);
  const contactsScope = usePageScope("contacts");
  const { branches } = useScopeOptions();
  const labelFilterName = searchParams.get("labelName")?.trim() || undefined;
  const [labelReloadToken, setLabelReloadToken] = useState(0);
  const { labels: scopeLabels, loading: loadingLabels } = useScopeLabels("contacts", {
    reloadToken: labelReloadToken,
  });
  // Clear All Filters spans the scope picker too: it registers with the same FilterScope,
  // so it counts towards the badge and has to be reset by the same control.
  const clearScope = useSettingsStore((s) => s.clearScope);
  useFilterUrlSync(list.filter, list.setFilter, contactFilterUrlSyncConfig);
  const clearAll = useCallback(() => {
    clearScope("contacts");
    list.setFilter(CLEARED_CONTACT_FILTER);
  }, [clearScope, list]);
  const selection = useContactSelection();
  const [selected, setSelected] = useState<ContactSummary | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [applyLabelOpen, setApplyLabelOpen] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  // A finished sweep is remembered so the link does not linger (and re-fetch)
  // when mid-sync rows kept the count below the filter's total.
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  // Contacts left out of the sweep because they were mid-sync, kept so the bar
  // can say why the count came out under the filter's total.
  const [skippedSyncing, setSkippedSyncing] = useState(0);
  /** Hidden while the deferred-delete undo window is active. */
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(() => new Set());

  const openCreateContact = useCallback(() => {
    setSelected(null);
    setDrawerOpen(true);
  }, []);
  useCreateShortcut(openCreateContact);

  const onDeleteScheduled = useCallback(
    (contactIds: string[]) => {
      setPendingDeleteIds((prev) => {
        const next = new Set(prev);
        for (const id of contactIds) next.add(id);
        return next;
      });
      selection.remove(contactIds);
      setSelected((current) => {
        if (current && contactIds.includes(current.id)) {
          setDrawerOpen(false);
          return null;
        }
        return current;
      });
    },
    [selection],
  );

  const onDeleteUndo = useCallback((contactIds: string[]) => {
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      for (const id of contactIds) next.delete(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (list.loading) startPageLoading();
    else stopPageLoading();
    return () => stopPageLoading();
  }, [list.loading, startPageLoading, stopPageLoading]);

  const selectionEmpty = selection.selectedIds.length === 0;
  useEffect(() => {
    setAllPagesSelected(false);
    setSkippedSyncing(0);
  }, [list.filter, selectionEmpty]);

  // Cross-module links (e.g. a lead's linked CRM contact) land here as `?contactId=`,
  // opening that contact's drawer directly — the id is consumed once so the drawer
  // can be closed and reopened without the param bringing it straight back.
  useEffect(() => {
    const openContactId = searchParams.get("contactId")?.trim();
    if (!openContactId) return;
    setSelected({ id: openContactId } as ContactSummary);
    setDrawerOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("contactId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const sortObj = useMemo(
    () =>
      list.pageRequest.sort && list.pageRequest.direction
        ? { field: list.pageRequest.sort, direction: list.pageRequest.direction }
        : undefined,
    [list.pageRequest.sort, list.pageRequest.direction],
  );

  const onSelect = (row: ContactSummary) => {
    setSelected(row);
    setDrawerOpen(true);
  };
  const onContactUpdate = (updated: ContactSummary) => {
    list.upsertEntry({
      ...(selected ?? {}),
      ...updated,
      id: updated.id ?? selected?.id ?? "",
    } as ContactSummary);
  };

  const onContactCreate = (created: ContactSummary) => {
    list.upsertEntry(created);
  };

  const tableSelection = useMemo<RowSelection<ContactSummary>>(
    () => ({
      selectedKeys: selection.selectedKeys,
      onToggle: selection.toggle,
      onToggleAll: selection.toggleAll,
      // A contact mid-sync would be re-created by the CRM moments after deletion,
      // so it cannot be picked at all.
      isSelectable: (row) => row.crmSyncStatus !== "IN_PROGRESS",
      selectAllLabel: "Select all contacts on this page",
    }),
    [selection.selectedKeys, selection.toggle, selection.toggleAll],
  );

  const branchToLegalEntity = useMemo(
    () => Object.fromEntries(branches.map((b) => [b.id, b.legalEntityId])),
    [branches],
  );
  const scopedLegalEntityIds = useMemo(
    () =>
      legalEntityIdsInScope(
        contactsScope.legalEntityIds,
        contactsScope.branchIds,
        branchToLegalEntity,
      ),
    [contactsScope.legalEntityIds, contactsScope.branchIds, branchToLegalEntity],
  );
  const campaignsLabelsEnabled = useCampaignsLabelsEnabled();
  const canManageLabels = campaignsLabelsEnabled && scopedLegalEntityIds.length === 1;

  const openManageLabels = () => {
    if (scopedLegalEntityIds.length === 0) {
      toast.error("Choose one legal entity in the filter before managing labels.");
      return;
    }
    if (scopedLegalEntityIds.length > 1) {
      toast.error(
        "Choose a single legal entity before managing labels. Labels cannot be applied across more than one entity.",
      );
      return;
    }
    setApplyLabelOpen(true);
  };

  const totalElements = list.data?.totalElements ?? 0;
  const selectableRows = useMemo(
    () =>
      (list.data?.content ?? []).filter(
        (row) => row.crmSyncStatus !== "IN_PROGRESS" && !pendingDeleteIds.has(row.id),
      ),
    [list.data?.content, pendingDeleteIds],
  );

  const tableRows = useMemo(
    () => (list.data?.content ?? []).filter((row) => !pendingDeleteIds.has(row.id)),
    [list.data?.content, pendingDeleteIds],
  );
  // The whole page is ticked but the filter matches more than what is selected,
  // so the operator is offered the rest of the pages in one go.
  const canSelectAllPages =
    selectableRows.length > 0 &&
    selectableRows.every((row) => selection.selectedKeys.has(row.id)) &&
    !allPagesSelected &&
    selection.selectedIds.length < totalElements;

  const selectAllPages = async () => {
    setSelectingAll(true);
    try {
      const rows = await list.fetchAllPages();
      const selectable = rows.filter((row) => row.crmSyncStatus !== "IN_PROGRESS");
      selection.toggleAll(selectable, true);
      setAllPagesSelected(true);

      // Fewer than the filter's total can end up ticked for two reasons, and the
      // operator is told which: rows mid-sync are not selectable at all, and the
      // `atlasModifiedAt desc` ordering can shift while the pages are fetched,
      // so a row that moves up is served twice and the one it displaced missed.
      const unique = new Set(selectable.map((row) => row.id)).size;
      const syncing = rows.length - selectable.length;
      setSkippedSyncing(syncing);
      if (unique < totalElements) {
        const reasons: string[] = [];
        if (syncing > 0) reasons.push(`${syncing} still syncing with the CRM`);
        const missed = totalElements - unique - syncing;
        if (missed > 0) reasons.push(`${missed} changed while loading`);
        toast.info(
          `Selected ${unique} of ${totalElements} contacts` +
            (reasons.length > 0 ? ` \u2014 ${reasons.join(", ")}.` : "."),
        );
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e) ?? "Could not select every contact.");
    } finally {
      setSelectingAll(false);
    }
  };

  const onDeleted = (impact: ContactDeletionImpact) => {
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      for (const id of impact.contactIds) next.delete(id);
      return next;
    });
    selection.remove(impact.contactIds);
    const warnings = Object.values(impact.warnings ?? {});
    if (warnings.length > 0) {
      toast.warning(
        `Deleted ${impact.contactIds.length} contact(s), but some cleanup did not complete: ${warnings.join(" ")}`,
      );
    } else {
      toast.success(
        `Deleted ${impact.contactIds.length} contact(s), ${impact.atlasRowsDeleted} related record(s) ` +
          `and ${impact.memoryNodesDeleted} memory node(s).`,
      );
    }
    list.reloadFirstPage();
    setDrawerOpen(false);
    setSelected(null);
  };

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Contacts"
        actions={
          <>
            <SavedFiltersButton
              pageName="contacts"
              filterParamKeys={CONTACT_FILTER_PARAM_KEYS}
              hasActiveFilterParams={hasActiveContactFilterParams}
              scopePage="contacts"
            />
            <RefreshButton onClick={list.reload} loading={list.loading} />
            <Tooltip content={`New contact (${formatPrimaryShortcut("k")})`} side="bottom">
              <Button
                variant="primary"
                size="sm"
                onClick={openCreateContact}
              >
                <Plus size={14} /> New contact
              </Button>
            </Tooltip>
          </>
        }
      />
      {/* Spans the bar and the table so the column filters in the header count
          towards the same badge, and Clear Filters resets both at once. */}
      <FilterScope onClear={clearAll}>
      <div className="flex-1 mx-6 mb-6 glass rounded-lg overflow-hidden flex flex-col">
        <ContactsFilters
          filter={list.filter}
          onChange={list.setFilter}
          onClear={clearAll}
          labelFilterName={labelFilterName}
          scopeLabels={scopeLabels}
          loadingLabels={loadingLabels}
          onScopeApply={() => {
            setLabelReloadToken((n) => n + 1);
            // The scope change already triggers its own refetch (fetcher picks up the
            // new scope automatically) — only force a second one when there's a stale
            // labelIds filter that actually needs clearing.
            if (list.filter.labelIds?.length) {
              list.setFilter({ labelIds: undefined });
            }
          }}
        />

        {selection.selectedIds.length > 0 && (
          <div className="flex items-center gap-3 border-b border-bridge-border/60 bg-bridge-accent-tint px-6 py-2.5">
            <span className="text-sm font-medium text-slate-700">
              {selection.selectedIds.length} selected
            </span>
            {canSelectAllPages && (
              <button
                type="button"
                onClick={() => void selectAllPages()}
                disabled={selectingAll}
                className="inline-flex items-center gap-1 text-xs font-medium text-bridge-accent hover:underline disabled:opacity-60"
              >
                {selectingAll && <Loader2 size={12} className="animate-spin" />}
                {selectingAll
                  ? "Selecting all contacts\u2026"
                  : `Select all ${totalElements} contacts`}
              </button>
            )}
            {canSelectAllPages && !selectingAll && (
              <span className="text-xs text-bridge-muted">
                Contacts still syncing with the CRM are skipped.
              </span>
            )}
            {!canSelectAllPages && totalElements > 0 &&
              (allPagesSelected || selection.selectedIds.length >= totalElements) && (
                <span className="text-xs text-bridge-muted">
                  All {selection.selectedIds.length} contacts selected
                  {skippedSyncing > 0
                    ? ` \u2014 ${skippedSyncing} skipped, still syncing with the CRM.`
                    : "."}
                </span>
              )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={selection.clear}
              className="inline-flex items-center gap-1 text-xs font-medium text-bridge-muted hover:text-bridge-fg"
            >
              <X size={12} /> Clear
            </button>
            {campaignsLabelsEnabled && (
              <Button
                variant="primary"
                size="sm"
                onClick={openManageLabels}
                disabled={!canManageLabels}
                title={
                  canManageLabels
                    ? undefined
                    : "Choose one legal entity in the filter before managing labels."
                }
              >
                <Tag size={14} /> Manage Labels
              </Button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-auto scrollbar-metal">
          <ContactsTable
            rows={tableRows}
            loading={list.loading}
            selectedKey={selected?.id ?? null}
            onSelect={onSelect}
            sort={sortObj}
            onSortChange={list.setSort}
            onResynced={list.reloadFirstPage}
            selection={tableSelection}
            filter={list.filter}
            onFilterChange={list.setFilter}
          />
        </div>
        <Pagination
          page={list.data?.page ?? 0}
          totalPages={list.data?.totalPages ?? 0}
          totalElements={list.data?.totalElements ?? 0}
          size={list.data?.size ?? 20}
          onPageChange={list.setPage}
        />
      </div>
      </FilterScope>

      <ContactApplyLabelDialog
        open={applyLabelOpen}
        contactIds={selection.selectedIds}
        labels={scopeLabels}
        loadingLabels={loadingLabels}
        onClose={() => setApplyLabelOpen(false)}
        onApplied={() => {
          selection.clear();
          list.reloadFirstPage();
        }}
      />

      <ContactDetailDrawer
        open={drawerOpen}
        contactId={selected?.id ?? null}
        onClose={() => {
          setDrawerOpen(false);
          setSelected(null);
        }}
        onSaved={list.reload}
        onUpdate={onContactUpdate}
        onCreate={onContactCreate}
        onDeleted={onDeleted}
        onDeleteScheduled={onDeleteScheduled}
        onDeleteUndo={onDeleteUndo}
      />

    </div>
  );
}
